import React, { useState, useRef, useEffect, useContext } from 'react';
import { LogoutOutlined, SettingOutlined } from '@ant-design/icons';
import { Radio, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import { FilterContextData } from 'contexts/FilterContext';
import { getAuthUser } from 'utils/helperMethods';
import { isAdminUser } from 'constants/admin';
import DaveImg from 'assets/images/Dave.png';
import JuliaImg from 'assets/images/Julia.png';
import TaylorImg from 'assets/images/Taylor.png';
import JordanImg from 'assets/images/Jordan.png';

const accountAvatarMap: Record<string, string> = {
    'Dave Anderson': DaveImg,
    'Julia Smith': JuliaImg,
    'Taylor Garcia': TaylorImg,
    'Jordan Williams': JordanImg,
};

const accountDetailsMap: Record<string, { name: string; role: string }> = {
    'Dave Anderson': {
        name: 'Dave Anderson',
        role: 'General Manager',
    },
    'Julia Smith': {
        name: 'Julia Smith',
        role: 'RGM Lead',
    },
    'Taylor Garcia': {
        name: 'Taylor Garcia',
        role: 'Brand Manager',
    },
    'Jordan Williams': {
        name: 'Jordan Williams',
        role: 'Key Account Manager',
    },
};

interface UserAccountManagerProps {
    logOut: () => void;
    className?: string;
    avatarClassName?: string;
    textClassName?: string;
}

// Temporary component for demo purpose.

export const UserAccountManager: React.FC<UserAccountManagerProps> = ({
    logOut,
    className = 'relative',
    avatarClassName = 'flex justify-center items-center rounded-full cursor-pointer bg-purple-100 avatar-size',
    textClassName = 'text-center text-purpleFont font-xxs font-bold',
}) => {
    const [profileWindow, setProfileWindow] = useState(false);
    const componentRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const profileSwitchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const { selectedAccount, setSelectedAccount, setIsProfileSwitching, setShowNotification } =
        useContext(FilterContextData);
    const navigate = useNavigate();
    const isAdmin = isAdminUser();

    const handleAccountChange = (e: any) => {
        const newAccount = e.target.value;

        // Only trigger loading if account is actually changing
        if (newAccount !== selectedAccount) {
            setIsProfileSwitching(true);

            // Clear any existing profile switch timeout
            if (profileSwitchTimeoutRef.current) {
                clearTimeout(profileSwitchTimeoutRef.current);
            }

            // After 3 seconds, switch the account and stop loading
            profileSwitchTimeoutRef.current = setTimeout(() => {
                setSelectedAccount(newAccount);
                setIsProfileSwitching(false);
            }, 3000);
        }

        // Close the popup immediately
        setProfileWindow(false);

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // If Julia Smith is selected, show notification after 5 seconds
        if (newAccount === 'Julia Smith') {
            timeoutRef.current = setTimeout(() => {
                setShowNotification(true);
            }, 5000);
        } else {
            // If Dave Anderson is selected, hide notification
            setShowNotification(false);
        }
    };

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (profileSwitchTimeoutRef.current) {
                clearTimeout(profileSwitchTimeoutRef.current);
            }
        };
    }, []);

    // Click outside handler
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (componentRef.current && !componentRef.current.contains(event.target as Node)) {
                setProfileWindow(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Get initials based on selected account
    const getInitials = (account: string): string => {
        if (account === 'Dave Anderson') return 'DA';
        if (account === 'Julia Smith') return 'JS';
        if (account === 'Taylor Garcia') return 'TG';
        if (account === 'Jordan Williams') return 'JW';
        // Fallback to authenticated user's initials if a different account/email is used
        return getAuthUser();
    };

    const getAvatarSrc = (account: string): string | null => {
        return accountAvatarMap[account] || null;
    };

    return (
        <div className={className} ref={componentRef}>
            {/* Account Selector Dropdown */}
            {profileWindow && (
                <div className="absolute right-0 top-full mt-2 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-[350px] min-w-[200px] max-w-[90vw] sm:w-[350px]">
                    {/* Header */}
                    <div className="text-gray-800 font-semibold text-sm mb-2">Accounts</div>

                    {/* Account Options with Custom Layout */}
                    <div className="space-y-2 mb-4">
                        <Radio.Group value={selectedAccount} onChange={handleAccountChange} className="w-full">
                            <div className="space-y-2">
                                <div
                                    className={`p-2 rounded transition-colors cursor-pointer ${
                                        selectedAccount === 'Dave Anderson'
                                            ? 'bg-[#F7F5FF]'
                                            : 'bg-white hover:bg-gray-50'
                                    }`}
                                    onClick={() => handleAccountChange({ target: { value: 'Dave Anderson' } })}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="text-gray-800 text-sm flex-1 pr-2 truncate">
                                            Dave Anderson, General Manager
                                        </span>
                                        <Radio value="Dave Anderson" className="[&_.ant-radio]:!m-0 flex-shrink-0" />
                                    </div>
                                </div>
                                <div
                                    className={`p-2 rounded transition-colors cursor-pointer ${
                                        selectedAccount === 'Taylor Garcia'
                                            ? 'bg-[#F7F5FF]'
                                            : 'bg-white hover:bg-gray-50'
                                    }`}
                                    // onClick={() => handleAccountChange({ target: { value: 'Julia Smith' } })}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="text-gray-800 text-sm flex-1 pr-2 truncate">
                                            Taylor Garcia, Brand Manager
                                        </span>
                                        <Radio value="Taylor Garcia" className="[&_.ant-radio]:!m-0 flex-shrink-0" />
                                    </div>
                                </div>

                                <div
                                    className={`p-2 rounded transition-colors cursor-pointer ${
                                        selectedAccount === 'Julia Smith' ? 'bg-[#F7F5FF]' : 'bg-white hover:bg-gray-50'
                                    }`}
                                    onClick={() => handleAccountChange({ target: { value: 'Julia Smith' } })}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="text-gray-800 text-sm flex-1 pr-2 truncate">
                                            Julia Smith, RGM Lead
                                        </span>
                                        <Radio value="Julia Smith" className="[&_.ant-radio]:!m-0 flex-shrink-0" />
                                    </div>
                                </div>
                                <div
                                    className={`p-2 rounded transition-colors cursor-pointer ${
                                        selectedAccount === 'Jordan Williams'
                                            ? 'bg-[#F7F5FF]'
                                            : 'bg-white hover:bg-gray-50'
                                    }`}
                                    // onClick={() => handleAccountChange({ target: { value: 'Julia Smith' } })}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="text-gray-800 text-sm flex-1 pr-2 truncate">
                                            Jordan Williams, Key Account Manager
                                        </span>
                                        <Radio value="Jordan Williams" className="[&_.ant-radio]:!m-0 flex-shrink-0" />
                                    </div>
                                </div>
                            </div>
                        </Radio.Group>
                    </div>

                    {/* Separator */}
                    <div className="border-t border-gray-200 mb-4"></div>

                    {/* Footer: settings (admin only) + logout */}
                    <div className={`flex items-center mt-4 ${isAdmin ? 'justify-between' : 'justify-center'}`}>
                        {isAdmin && (
                            <Tooltip placement="top" title="Admin Settings">
                                <div
                                    className="w-9 h-9 flex justify-center items-center bg-purple-100 rounded cursor-pointer hover:bg-purple-200"
                                    onClick={() => {
                                        setProfileWindow(false);
                                        navigate('/admin');
                                    }}
                                >
                                    <SettingOutlined className="text-purpleFont text-lg" />
                                </div>
                            </Tooltip>
                        )}
                        <div
                            className={`w-[180px] py-1 bg-purpleFont rounded flex justify-center items-center cursor-pointer max-w-[calc(100%-2rem)] sm:w-[180px] ${
                                isAdmin ? '' : 'mx-auto'
                            }`}
                            onClick={logOut}
                        >
                            <span className="text-white">Logout</span>
                        </div>
                    </div>
                </div>
            )}
            {/* Avatar Button */}
            {/* <span className={avatarClassName} onClick={() => setProfileWindow((prev) => !prev)}>
                <span className={textClassName}>{getInitials(selectedAccount)}</span>
            </span> */}
            <Tooltip
                placement="bottom"
                color="#ffffff"
                overlayInnerStyle={{
                    borderRadius: '8px',
                    padding: '8px 12px',
                }}
                title={
                    <div className="text-center">
                        <div className="font-semibold text-sm text-gray-900">
                            {accountDetailsMap[selectedAccount]?.name}
                        </div>
                        <div className="text-xs text-gray-500">{accountDetailsMap[selectedAccount]?.role}</div>
                    </div>
                }
            >
                <span className={avatarClassName} onClick={() => setProfileWindow((prev) => !prev)}>
                    {getAvatarSrc(selectedAccount) ? (
                        <img
                            src={getAvatarSrc(selectedAccount)!}
                            alt={selectedAccount}
                            className="w-full h-full rounded-full object-cover"
                        />
                    ) : (
                        <span className={textClassName}>{getAuthUser()}</span>
                    )}
                </span>
            </Tooltip>
        </div>
    );
};

export default UserAccountManager;
